import { useApolloClient } from "@apollo/client/react";
import { useClerk } from "@clerk/clerk-react";
import { Fragment, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BriefcaseIcon,
  Building2Icon,
  IdCardIcon,
  LogInIcon,
  MailIcon,
  PlusIcon,
  UserCogIcon,
} from "lucide-react";
import { ExternalGrantStatusEnum } from "@/__generated__/graphql";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { useExternalGrants } from "@/components/organisms/ExternalGrants/ExternalGrants.hooks";
import { usePendingInvitations } from "@/components/organisms/Invitations/Invitations.hooks";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { icons } from "@/objects/@icons";
import { SETTINGS_RETURN_URL_KEY } from "@/layouts/AppLayout/AppSidebar/SettingsSidebarHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

function MenuDivider() {
  return <div className="mt-1 mb-1 w-full h-px bg-border" />;
}

export function AppSidebarEntitySettingsPopper() {
  const {
    user,
    userEntity,
    accounts,
    isLoadingUser,
    isLoadingAccounts,
    isLoading,
  } = useScope();
  const { pendingInvitations } = usePendingInvitations();
  // Grants given them - or asked of them - make a portal of their clients.
  const { received: receivedGrants, asked } = useExternalGrants();
  const pendingGrantCount = asked.length;
  const hasClients = receivedGrants.some(
    (grant) => grant.grantStatus === ExternalGrantStatusEnum.Active,
  );
  const invitationCount = pendingInvitations.length + pendingGrantCount;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSidebarMinimized = State((state) => state.isSidebarMinimized);

  const { signOut } = useClerk();
  const { cache } = useApolloClient();
  const [isOpen, setIsOpen] = useState(false);

  // Prefer the accounts query; fall back to the user payload so the menu is
  // never an empty "New entity / Sign out" stub while scope is still settling.
  const menuAccounts = useMemo(() => {
    if (accounts && accounts.length > 0) {
      return accounts;
    }
    return user?.accounts ?? [];
  }, [accounts, user?.accounts]);

  return (
    <Popper
      isOpenState={[isOpen, setIsOpen]}
      mainAxisOffset={8}
      crossAxisOffset={8}
      isClickable={false}
      autoFocus={false}
      anchor={
        <div className="p-2 w-full h-12 flex items-center flex-nowrap justify-start overflow-hidden gap-2">
          <div
            onClick={() => setIsOpen(!isOpen)}
            // Minimized, the box alone sits in the middle of the rail, as
            // the icons under it do; its inset would push it right.
            className={cn(
              "w-full flex items-center flex-nowrap",
              isSidebarMinimized ? "justify-center" : "justify-start",
              "overflow-hidden cursor-pointer gap-2 text-left",
              isSidebarMinimized ? "pr-0" : "pr-1.5",
              isSidebarMinimized ? "pl-0" : "pl-1.5",
              "pt-1 pb-1 rounded-sm [box-shadow:none]",
              isOpen ? "bg-secondary" : "bg-transparent",
              "hover:bg-secondary",
            )}
          >
            <BoxForEntity
              identifier={userEntity?.identifier}
              representation="character"
              // A button's size, as the top bar's beside it are.
              size="lg"
            />
            {!isSidebarMinimized && (
              <>
                <Typography
                  level="title-md"
                  className="truncate text-ellipsis whitespace-nowrap overflow-hidden inline-block items-center flex-nowrap w-full rounded-[2px]"
                >
                  <Skeleton
                    loading={isLoadingUser || isLoadingAccounts}
                    className="max-w-full"
                  >
                    {userEntity?.name ||
                      userEntity?.identifier ||
                      ((isLoadingUser || isLoadingAccounts) &&
                        "Loading entity...")}
                  </Skeleton>
                </Typography>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className={cn(
                    "bg-transparent origin-center [transition:transform_0.2s_ease-in-out]",
                    isOpen
                      ? "transform-[translateY(1px)]"
                      : "transform-[translateY(0px)]",
                    "hover:bg-transparent hover:transform-[translateY(1px)]",
                  )}
                >
                  <Icon ReactIcon={icons.chevronDown} />
                </Button>
              </>
            )}
          </div>
        </div>
      }
      style={{
        width: "100%",
      }}
      placement="bottom-start"
      floatingStyle={{ paddingTop: "4px", minWidth: "320px" }}
    >
      {isLoading && menuAccounts.length === 0 && (
        <div className="pl-3 pr-3 pt-2 pb-2 flex flex-col gap-2">
          <Skeleton variant="rectangular" height={16} width="60%" />
          <Skeleton variant="rectangular" height={28} width="100%" />
          <Skeleton variant="rectangular" height={28} width="100%" />
        </div>
      )}

      {menuAccounts.map((account, index) => {
        const entity = account.entity;
        if (!entity) {
          return null;
        }

        return (
          <Fragment key={account.id ?? entity.id ?? entity.identifier}>
            {index > 0 && <MenuDivider />}

            <div className="pl-2 pr-2 w-full flex items-center justify-start pt-1 pb-1">
              <Typography
                level="title-sm"
                className="text-muted-foreground/80 pl-2"
              >
                {(entity.name || entity.identifier) +
                  (account.isUserAccount ? " (you)" : "")}
              </Typography>
            </div>

            <div className="pl-1 pr-1 w-full flex items-center justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsOpen(false);
                  sessionStorage.setItem(SETTINGS_RETURN_URL_KEY, pathname);
                  navigate(`/app/settings/entity/${entity.identifier}`);
                }}
                className="text-left justify-start [box-shadow:none] w-full"
              >
                {account.isUserAccount ? (
                  <UserCogIcon size="1em" />
                ) : (
                  <Building2Icon size="1em" />
                )}
                Settings
              </Button>
            </div>

            {account.isUserAccount && (
              <div className="pl-1 pr-1 w-full flex items-center justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsOpen(false);
                    sessionStorage.setItem(SETTINGS_RETURN_URL_KEY, pathname);
                    navigate("/app/settings/profile");
                  }}
                  className="text-left justify-start [box-shadow:none] w-full"
                >
                  <IdCardIcon size="1em" />
                  Profile
                </Button>
              </div>
            )}
          </Fragment>
        );
      })}

      {(menuAccounts.length > 0 || invitationCount > 0 || hasClients) && (
        <MenuDivider />
      )}

      {hasClients && (
        <div className="pl-1 pr-1 w-full flex items-center justify-center">
          <Button
            variant="ghost"
            size="sm"
            data-testid="account-menu-clients"
            onClick={() => {
              setIsOpen(false);
              navigate("/app/clients");
            }}
            className="text-left justify-start [box-shadow:none] w-full"
          >
            <BriefcaseIcon />
            Your clients
          </Button>
        </div>
      )}

      {invitationCount > 0 && (
        <div className="pl-1 pr-1 w-full flex items-center justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              navigate("/app/invitations");
            }}
            className="text-left justify-start [box-shadow:none] w-full"
          >
            <MailIcon />
            Invitations ({invitationCount})
          </Button>
        </div>
      )}

      <div className="pl-1 pr-1 w-full flex items-center justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setIsOpen(false);
            State.getState().pushToCommandStack({
              commandDefinitionId: "create-EntityObject",
            });
          }}
          className="text-left justify-start [box-shadow:none] w-full"
        >
          <PlusIcon />
          New entity
        </Button>
      </div>

      <MenuDivider />

      <div className="p-1 pt-0 w-full flex items-center justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setIsOpen(false);
            cache.reset();
            signOut();
            localStorage.removeItem("global-store");
          }}
          className="text-left justify-start [box-shadow:none] w-full"
        >
          <LogInIcon />
          Sign out
        </Button>
      </div>
    </Popper>
  );
}
